import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import {
  SaveIftaTenantProfileRequest,
  useGetIftaProfileQuery,
  useSaveIftaProfileMutation,
} from "../../api/reportsApi";

interface IftaProfileDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const emptyForm: SaveIftaTenantProfileRequest = {
  legalName: "",
  dbaName: "",
  iftaAccountNumber: "",
  addressLine1: "",
  cityStateZip: "",
  phone: "",
};

const IftaProfileDialog: React.FC<IftaProfileDialogProps> = ({
  open,
  onClose,
  onSaved,
}) => {
  const { data: profile, isLoading } = useGetIftaProfileQuery(undefined, { skip: !open });
  const [saveProfile, { isLoading: saving }] = useSaveIftaProfileMutation();

  const [form, setForm] = useState<SaveIftaTenantProfileRequest>(emptyForm);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !profile) return;
    setForm({
      legalName: profile.legalName ?? "",
      dbaName: profile.dbaName ?? "",
      iftaAccountNumber: profile.iftaAccountNumber ?? "",
      addressLine1: profile.addressLine1 ?? "",
      cityStateZip: profile.cityStateZip ?? "",
      phone: profile.phone ?? "",
    });
    setError(null);
  }, [open, profile]);

  const handleChange = (field: keyof SaveIftaTenantProfileRequest, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setError(null);

    if (!form.iftaAccountNumber.trim()) {
      setError("IFTA account number is required.");
      return;
    }
    if (!form.addressLine1.trim()) {
      setError("Physical address is required.");
      return;
    }
    if (!form.cityStateZip.trim()) {
      setError("City, state and ZIP are required.");
      return;
    }
    if (!form.phone.trim()) {
      setError("Telephone number is required.");
      return;
    }

    try {
      await saveProfile(form).unwrap();
      onSaved();
    } catch (err) {
      setError(
        err && typeof err === "object" && "data" in err
          ? String((err as { data?: { message?: string } }).data?.message ?? "Save failed.")
          : "Save failed."
      );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 2 }}>
        <Typography component="div" variant="subtitle1" fontWeight={600}>
          IFTA company information
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1.5, fontWeight: 400, lineHeight: 1.5 }}
        >
          Review the details that appear on the report header. Update anything that changed —
          your company info is saved and reused on future reports.
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 3, px: 3 }}>
        {isLoading ? (
          <CircularProgress size={24} />
        ) : (
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Legal name"
                helperText="Leave blank to use your company name from ADUT"
                value={form.legalName}
                onChange={(e) => handleChange("legalName", e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="DBA name (optional)"
                placeholder="/"
                value={form.dbaName}
                onChange={(e) => handleChange("dbaName", e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                required
                label="IFTA account number"
                value={form.iftaAccountNumber}
                onChange={(e) => handleChange("iftaAccountNumber", e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                required
                label="Physical address"
                value={form.addressLine1}
                onChange={(e) => handleChange("addressLine1", e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                required
                label="City, state and ZIP"
                placeholder="Mt. Prospect, IL 60056"
                value={form.cityStateZip}
                onChange={(e) => handleChange("cityStateZip", e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                required
                label="Telephone"
                value={form.phone}
                onChange={(e) => handleChange("phone", e.target.value)}
              />
            </Grid>
          </Grid>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || isLoading}>
          {saving ? <CircularProgress size={18} color="inherit" /> : "Save and continue"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IftaProfileDialog;
